import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { Link } from 'react-router-dom'
import {
  ChevronDownIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { useAuthStore } from '@/store/auth'
import { useLogout } from '@/hooks/useAuth'
import Avatar from '@/components/ui/Avatar'

export default function Navbar() {
  const user = useAuthStore((state) => state.user)
  const logout = useLogout()

  return (
    <header className="bg-surface border-b border-dark-700 sticky top-0 z-40">
      <div className="flex items-center justify-end h-16 px-6">
        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* User menu */}
          <Menu as="div" className="relative">
            <Menu.Button
              className={clsx(
                'flex items-center gap-2 p-1.5 pr-3 rounded-lg',
                'hover:bg-dark-700',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                'transition-colors duration-200'
              )}
            >
              <Avatar
                src={user?.avatar_url}
                name={user?.name || 'User'}
                size="sm"
              />
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-cream leading-tight">
                  {user?.name}
                </p>
                <p className="text-xs text-cream-muted leading-tight capitalize">
                  {user?.status || 'Active'}
                </p>
              </div>
              <ChevronDownIcon className="h-4 w-4 text-dark-400 ml-1" aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                className={clsx(
                  'absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-lg',
                  'bg-surface border border-dark-600 shadow-hard',
                  'py-1 focus:outline-none'
                )}
              >
                {/* User info header */}
                <div className="px-4 py-3 border-b border-dark-700">
                  <p className="text-sm font-medium text-cream">{user?.name}</p>
                  <p className="text-xs text-cream-muted truncate">{user?.email}</p>
                </div>

                {/* Menu items */}
                <div className="py-1">
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/profile"
                        className={clsx(
                          'flex items-center gap-3 px-4 py-2 text-sm',
                          active ? 'bg-dark-700 text-cream' : 'text-cream-muted'
                        )}
                      >
                        <UserCircleIcon className="h-5 w-5" />
                        Profile
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/settings"
                        className={clsx(
                          'flex items-center gap-3 px-4 py-2 text-sm',
                          active ? 'bg-dark-700 text-cream' : 'text-cream-muted'
                        )}
                      >
                        <Cog6ToothIcon className="h-5 w-5" />
                        Settings
                      </Link>
                    )}
                  </Menu.Item>
                </div>

                {/* Logout */}
                <div className="py-1 border-t border-dark-700">
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={logout}
                        className={clsx(
                          'flex items-center gap-3 w-full px-4 py-2 text-sm',
                          active ? 'bg-error/10 text-error' : 'text-cream-muted'
                        )}
                      >
                        <ArrowRightOnRectangleIcon className="h-5 w-5" />
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </header>
  )
}
